import { h, defineAsyncComponent, type Ref } from 'vue'
import type { CookieParseOptions, CookieSerializeOptions } from 'cookie-es'
import type { Dayjs } from 'dayjs'
import euList from '../assets/data/euList.json'
import { toast } from '~/utils/sonner'
type _CookieOptions = Omit<CookieSerializeOptions & CookieParseOptions, 'decode' | 'encode'>

export interface CookieOptions<T = any> extends _CookieOptions {
  decode?(value: string): T
  encode?(value: T): string
  default?: () => T | Ref<T>
  watch?: boolean | 'shallow'
}

export default () => {
  // console.log('-->>>>>context is', NuxtApp)
  // const NuxtApp = useNuxtApp()
  // const { $i18n: i18n } = useNuxtApp()
  const i18n = useI18n()
  const t = i18n.t
  const dayjs = useDayjs()
  const ua = () => {
    if (useIsBrowser()) {
      return navigator?.userAgent.toLowerCase() || ''
    } else {
      return ''
    }
  }

  const nameMap = new Map([
    [23, ['Sonoma', '14']],
    [22, ['Ventura', '13']],
    [21, ['Monterey', '12']],
    [20, ['Big Sur', '11']],
    [19, ['Catalina', '10.15']],
    [18, ['Mojave', '10.14']],
    [17, ['High Sierra', '10.13']],
    [16, ['Sierra', '10.12']],
    [15, ['El Capitan', '10.11']],
    [14, ['Yosemite', '10.10']],
    [13, ['Mavericks', '10.9']],
    [12, ['Mountain Lion', '10.8']],
    [11, ['Lion', '10.7']],
    [10, ['Snow Leopard', '10.6']],
    [9, ['Leopard', '10.5']],
    [8, ['Tiger', '10.4']],
    [7, ['Panther', '10.3']],
    [6, ['Jaguar', '10.2']],
    [5, ['Puma', '10.1']],
  ])

  function macosRelease(release) {
    release = Number(release.split('.')[0])

    const [name, version] = nameMap.get(release) || ['Unknown', '']

    return {
      name,
      version,
    }
  }

  const cookieOptions: CookieOptions<string | null> = {
    expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 180),
    sameSite: 'none',
    secure: true,
    path: '/',
  }

  const getCookieOptions = () => {
    const isSafari =
      // @ts-expect-error
      (typeof window !== 'undefined' && useIsBrowser() && /constructor/i.test(window?.HTMLElement)) ||
      (function (p) {
        return p.toString() === '[object SafariRemoteNotification]'
        // @ts-expect-error
      })(!window?.safari || (typeof safari !== 'undefined' && window?.safari.pushNotification))
    const runtimeConfig = useRuntimeConfig().public
    // if (runtimeConfig.dev && ua().inclu) {
    // }
    // check if ua is safari
    // console.log('isSafari', isSafari)

    if (runtimeConfig.dev && isSafari) {
      console.log('setting cookie options for local dev safari')
      // return undefined
      // return cookieOptions
      const opt = {
        expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 180),
        // sameSite: 'none',
        // secure: false,
        path: '/',
        // expires,
        // secure: false,
        // httpOnly: true,
        // sameSite: 'none',
        // path: "/",
      } as CookieOptions
      console.log('cookie options set to undefined', opt)
      return opt
    }
    return cookieOptions
  }

  const cookieParse = () => {
    if (useIsBrowser()) {
      const ret: any = {}
      document.cookie.split(';').forEach((c) => {
        const [key, val] = c.trim().split('=').map(decodeURIComponent) as [string, string]
        try {
          ret[key] = JSON.parse(val)
        } catch (e) {
          ret[key] = val
        }
      })
      return ret
    } else {
      return {}
    }
  }
  const eurToRmb = (eur: number) => {
    const runtimeConfig = useRuntimeConfig().public
    if (!eur) {
      return 0
    }
    return moneyShape(eur * runtimeConfig.rmbToEurRate)
  }
  const toEur = (rmb: number) => {
    const runtimeConfig = useRuntimeConfig().public
    if (!rmb) {
      return 0
    }
    return moneyShape((rmb / runtimeConfig.rmbToEurRate + 0.25) / 0.971)
  }
  const moneyShape = (input: number) => {
    return Math.ceil(input * 100) / 100
  }
  const legacyCopy = (content = '') => {
    const textArea = document.createElement('textarea')
    textArea.value = content
    textArea.style.position = 'fixed'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
  }
  const utils = {
    hello: (msg: string) => console.log(`Hello ${msg}!`),
    money: (input: number | string) => Math.floor(Number(input) * 100) / 100,
    toDate: (input: string | Date | number) => new Date(input),
    isEu: (countryName: string) => euList.includes(countryName),
    checkToken: (token: any) => token !== 'undefined' && token !== 'null' && token,
    dateStr: (input?: number | Date | string, inBeijingTime = false) => {
      if (!input) {
        return ''
      }
      const format = dayjs(input).isSame(dayjs(), 'year') ? 'M/D HH:mm:ss Z' : 'YYYY/M/D HH:mm:ss Z'
      return inBeijingTime ? dayjs(input).tz('Asia/Shanghai').format(format) : dayjs(input).format(format)
    },
    dateStrShort: (input?: number | Date | string, inBeijingTime = false) => {
      if (!input) {
        return ''
      }
      const format = dayjs(input).isSame(dayjs(), 'year') ? 'M/D' : 'YYYY/M/D'
      return inBeijingTime ? dayjs(input).tz('Asia/Shanghai').format(format) : dayjs(input).format(format)
    },
    futureTime: (_pastT: number | Date | string | Dayjs, inBeijingTime: boolean) => {
      const now = dayjs()
      const pastT = dayjs(_pastT)
      if (inBeijingTime) {
        pastT.tz('Asia/Shanghai')
        now.tz('Asia/Shanghai')
      }
      return pastT.to(now)
    },
    relativeTime: (_futureT: number | Date | string | Dayjs | null | undefined, inBeijingTime = false) => {
      // console.log('relativeTime: dayjs is', dayjs)
      if (!_futureT) {
        return ''
      }
      const now = dayjs()
      const futureT = dayjs(_futureT)
      if (inBeijingTime) {
        futureT.tz('Asia/Shanghai')
        now.tz('Asia/Shanghai')
      }
      return now.to(futureT)
    },
    currency: (amount: number | string | null | undefined, currency = 'EUR') => {
      const currencyCode = String(currency || 'EUR').toUpperCase()
      const normalizedAmount = typeof amount === 'number'
        ? amount
        : typeof amount === 'string' && amount.trim()
          ? Number(amount)
          : 0
      const hasNumericAmount = Number.isFinite(normalizedAmount)

      if (typeof Intl !== 'undefined') {
        try {
          return new Intl.NumberFormat(i18n.getBrowserLocale(), {
            style: 'currency',
            currency: currencyCode,
          }).format(hasNumericAmount ? normalizedAmount : 0)
        } catch (e) {}
      }
      const signMapping: Record<string, string> = {
        CNY: '¥',
        EUR: '€',
        USD: '$',
      }
      const sign = signMapping[currencyCode] || currencyCode
      return `${sign} ${hasNumericAmount ? normalizedAmount.toFixed(2) : amount || 0}`
    },
    getDaysInMonth: (dayjsObj) => {
      return new Date(dayjsObj.year(), dayjsObj.month() + 1, 0).getDate()
    },
    openSite: (u: string) => {
      const ret = window.open(u, '_blank')
      if (!ret) {
        location.href = u
        return 'self'
      }
      return '_blank'
    },
    inspectObject: (obj: any) => {
      // Use the new ObjectInspectDisplay component
      const ObjectInspectDisplay = defineAsyncComponent(() => import('@/components/ObjectInspectDisplay.vue'))

      return useDialog().alert({
        title: 'Object Inspect',
        body: h(ObjectInspectDisplay, { data: obj }),
        confirmButtonText: 'Close',
        confirmButtonClass: 'w-full',
        dialogContentClass: 'w-full md:w-[95vw] max-w-7xl max-h-[80vh] !overflow-hidden p-0 flex flex-col',
      })
    },
    legacyCopy,
    copy: async (content?: string) => {
      if (!content || !useIsBrowser()) {
        return
      }
      try {
        if (!navigator?.clipboard) {
          throw new Error('navigator?.clipboard is not available')
        }
        await navigator!.clipboard.writeText(content)
      } catch (e) {
        try {
          legacyCopy(content)
        } catch (err) {
          return toast({
            title: t('copy_failed'),
            description: JSON.stringify(err),
            variant: 'destructive',
          })
        }
      }
      toast({
        title: t('copied'),
        description: content as string,
      })
    },
    stringifyData: (data: any, channel: string, urlencode: any) => {
      const hasOwn = {}.hasOwnProperty
      if (typeof urlencode === 'undefined') {
        urlencode = false
      }
      const output = [] as string[]
      let i: string
      for (i in data) {
        if (!hasOwn.call(data, i) || typeof data[i] === 'function') {
          continue
        }
        if (channel === 'bfb_wap' && i === 'url') {
          continue
        }
        if (channel === 'yeepay_wap' && i === 'mode') {
          continue
        }
        if (i === 'channel_url') {
          continue
        }
        output.push(i + '=' + (urlencode ? encodeURIComponent(data[i]) : data[i]))
      }
      return output.join('&')
    },
    ua,
    isInWeChat() {
      return /micromessenger/i.test(ua())
    },
    toType(obj: any) {
      // @ts-expect-error
      return {}.toString
        .call(obj)
        .match(/\s([a-zA-Z]+)/)[1]
        .toLowerCase()
    },
    capitalize: (str: string) => str?.charAt(0)?.toUpperCase() + str?.slice(1),
    stringToColorHex: (str: string) => {
      if (!str) {
        return 'red'
      }
      let hash = 0
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash)
      }
      let colour = '#'
      for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xff
        colour += ('00' + value.toString(16)).slice(-2)
      }
      return colour
    },
    toCurrencyString: (amount: number, currency: string) => {
      if (currency === 'CNY') {
        if (Intl) {
          try {
            return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY' }).format(eurToRmb(amount))
          } catch (e) {}
        }
        return `¥ ${amount.toFixed(2)}`
      }
      // EUR
      if (Intl) {
        // console.log('===>>>> Intl is available.')
        try {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
        } catch (e) {}
      }
      return `€ ${amount.toFixed(2)}`
    },
    geoStr(geoObj: IPLocation) {
      if (!geoObj) {
        return ''
      }
      return Array.from(
        new Set([
          geoObj.countryName === '中国' ? '' : geoObj.countryName || '',
          geoObj.countryName === '中国' ? geoObj.regionName || '' : '',
          geoObj.cityName || '',
        ]),
      )
        .filter((item) => item?.length)
        .join(' ')
    },
    eurToRmb,
    moneyShape,
    toEur,
    wait: (ms: number) => new Promise((resolve) => setTimeout(resolve, ms)),
    cookies: {
      get: (name: string) => {
        return useCookie(name, getCookieOptions())
      },
      set: (name: string, value: any, options = {}) => {
        console.log('set cookie:', name, value, options)
        const cookieItem = useCookie(name, { ...getCookieOptions(), ...options })
        console.log('cookieItem:', cookieItem)
        cookieItem.value = value
        console.log(cookieItem.value)
      },
      remove: (name: string, options = {}) => {
        useCookie(name, getCookieOptions()).value = null
      },
    },
    cookiz: {
      get: (name?: string) => {
        if (name) {
          return cookieParse()[name]
        }
        return cookieParse()
      },
      set: (name: string, value: any, options = {}) => {},
    },
    alertWeChat() {
      const isInWeChat = () => {
        return /micromessenger/i.test(ua())
      }
      // console.log('this.isInWeChat():', this.isInWeChat())
      if (useIsBrowser() && isInWeChat()) {
        try {
          useDialog().alert({
            title: t('guide.wechat-compatibility.title'),
            body: t('guide.wechat-compatibility.msg'),
            confirmButtonText: t('ok'),
          })
        } catch (e) {
          alert(`微信浏览器不支持导入链接，请使用系统自带、Safari、Chrome浏览器打开本页面导入设定。`)
        }
      }
    },
    getQRImageURL(text: string) {
      return `${useRuntimeConfig().public.qrCodeWorker}?text=${encodeURIComponent(text)}`
    },
    macosRelease,
    serverNameSorter(a: string, b: string): number {
      try {
        const matchA = a?.match?.(/([a-z]*)(\d+)/)
        const matchB = b?.match?.(/([a-z]*)(\d+)/)
        const strA = (matchA?.[1] || a)?.toUpperCase?.()
        const numA = Number(matchA?.[2])
        const strB = (matchB?.[1] || b)?.toUpperCase?.()
        const numB = Number(matchB?.[2])
        if (!numA || !numB) {
          return strA?.[0]?.localeCompare(strB?.[0] || '') || 0
        }
        if (strA === strB) {
          return numA - numB
        }
        if (strA.startsWith('CN') && !strB.startsWith('CN')) {
          return -1
        } else if (!strA.startsWith('CN') && strB.startsWith('CN')) {
          return 1
        }
        // if (strA.length === strB.length) {
        // }
        // const lengthDiff = strB.length - strA.length
        // if (lengthDiff !== 0) {
        //   return lengthDiff
        // }
        // return a.localeCompare(b)
        return strA.localeCompare(strB)
      } catch (e) {
        console.log(e, 'A, B is', a, b)
      }
      return 0
    },
    processTrAmount(total: number, startFrom = 1) {
      const measures = ['KB', 'MB', 'GB', 'TB']
      let measure = startFrom
      while (total > 1024) {
        total = Math.round((total / 1024) * 1000) / 1000
        measure += 1
      }
      if (total < 1) {
        total = Math.round(total * 1024 * 1000) / 1000
        measure -= 1
      }
      let ret: string | number
      if (!total) {
        ret = 0
      } else {
        ret = total.toFixed(measure > 1 ? 1 : 0)
      }
      return `${ret}${measures[measure] || ''}`
    },
    // 向下放大显示流量；默认输入单位是GB
    trFormat(input: number, metric = 'GB') {
      let final = input
      if (input < 1) {
        final = input * 1024
        metric = 'MB'
      }
      return Math.round(final * 100) / 100 + metric
    },
    apiErrorText(err: any) {
      if (!err) return ''
      const dataMsg = err?.data?.message
      if (typeof dataMsg === 'string' && dataMsg.trim()) return dataMsg
      if (dataMsg != null && typeof dataMsg === 'object') {
        try {
          const s = JSON.stringify(dataMsg)
          if (s && s !== '{}' && s !== '[]') return s
        } catch {}
      }
      const msg = err?.message || err?.statusMessage || err?.statusText
      if (typeof msg === 'string' && msg.trim()) return msg
      const detailMsg = err?.data?.detail?.message
      if (typeof detailMsg === 'string' && detailMsg.trim()) return detailMsg
      try {
        const s = JSON.stringify(err)
        if (s && s !== '{}' && s !== '[]') return s
      } catch {}
      return String(err)
    },
  }
  return utils
}
